import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { engineerService, EngineerJob, Visit } from '../../services/engineerService';

const statusLabel: Record<string, string> = {
  EN_ROUTE: 'On the way',
  ON_SITE: 'On site',
  PAUSED: 'Paused',
  SCHEDULED: 'Scheduled',
  ENDED: 'Done',
  CANCELLED: 'Cancelled',
};

const statusColor: Record<string, string> = {
  EN_ROUTE: 'bg-amber-100 text-amber-800',
  ON_SITE: 'bg-emerald-100 text-emerald-800',
  PAUSED: 'bg-orange-100 text-orange-800',
  SCHEDULED: 'bg-slate-100 text-slate-700',
  ENDED: 'bg-slate-100 text-slate-500',
  CANCELLED: 'bg-slate-100 text-slate-500',
};

function formatTime(iso?: string | null) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const ActiveVisitCard: React.FC<{ visit: Visit & { job?: EngineerJob } }> = ({ visit }) => {
  const job = (visit as any).job as EngineerJob | undefined;
  return (
    <Link
      to={`/engineer/jobs/${visit.jobId}`}
      className="block bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl p-4 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide opacity-80">
          {statusLabel[visit.status] || visit.status}
        </span>
        <span className="text-xs opacity-80">
          Since {formatTime(visit.arrivedAt || visit.enRouteAt || visit.pausedAt)}
        </span>
      </div>
      <div className="mt-2 text-base font-semibold line-clamp-2">
        {job?.title || 'Active job'}
      </div>
      <div className="mt-1 text-sm opacity-90 truncate">
        {job?.client?.name}{job?.address ? ` · ${job.address}` : ''}
      </div>
    </Link>
  );
};

const JobRow: React.FC<{ job: EngineerJob }> = ({ job }) => {
  const activeVisit = (job.visits || []).find((v) =>
    ['EN_ROUTE', 'ON_SITE', 'PAUSED'].includes(v.status),
  );

  return (
    <Link
      to={`/engineer/jobs/${job.id}`}
      className="block bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-blue-300 transition-colors"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500">
          {job.scheduledDate ? formatTime(job.scheduledDate) : 'Any time'}
          {job.jobType ? ` · ${job.jobType}` : ''}
        </span>
        {activeVisit ? (
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusColor[activeVisit.status]}`}>
            {statusLabel[activeVisit.status] || activeVisit.status}
          </span>
        ) : (
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusColor[job.status] || 'bg-slate-100 text-slate-600'}`}>
            {statusLabel[job.status] || job.status}
          </span>
        )}
      </div>
      <div className="mt-1.5 text-base font-semibold text-slate-900 line-clamp-2">
        {job.title}
      </div>
      <div className="mt-1 text-sm text-slate-600 truncate">
        {job.client?.name}
      </div>
      {(job.address || job.postcode) && (
        <div className="mt-0.5 text-xs text-slate-500 truncate">
          {[job.address, job.postcode].filter(Boolean).join(' · ')}
        </div>
      )}
    </Link>
  );
};

const EngineerToday: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['engineer', 'today'],
    queryFn: () => engineerService.getToday(),
    refetchInterval: 60_000,
  });

  const dateLabel = useMemo(() => {
    return new Date().toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  }, []);

  return (
    <div className="max-w-xl mx-auto px-4 py-4 space-y-4">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Today</div>
        <div className="text-lg font-semibold text-slate-900">{dateLabel}</div>
      </div>

      {isLoading && <div className="text-sm text-slate-500">Loading today's work…</div>}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          Couldn't load your jobs. Pull to refresh.
        </div>
      )}

      {data?.activeVisits?.length ? (
        <div className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">In progress</div>
          {data.activeVisits.map((v) => (
            <ActiveVisitCard key={v.id} visit={v as any} />
          ))}
        </div>
      ) : null}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Scheduled</div>
          <div className="text-xs text-slate-400">
            {data?.jobs?.length || 0} job{data?.jobs?.length === 1 ? '' : 's'}
          </div>
        </div>

        {data && data.jobs.length === 0 && !isLoading && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
            <div className="text-sm font-semibold text-slate-700">No jobs scheduled</div>
            <div className="text-xs text-slate-500 mt-1">Enjoy the quiet or check upcoming.</div>
          </div>
        )}

        {data?.jobs.map((j) => (
          <JobRow key={j.id} job={j} />
        ))}
      </div>
    </div>
  );
};

export default EngineerToday;
